import type { RepositoryPermission } from '@/lib/github/graphql/types';

import { MergeStateStatus } from '@/lib/github/graphql/types';

const getHumanReadableMergeStatus = (
  status: MergeabilityFacts,
  inProgressRequiredChecks: readonly string[],
) => {
  if (status.state.merged) {
    return 'Merged';
  }
  if (status.state.prState === 'closed') {
    return 'Closed';
  }
  if (status.draft) {
    return 'Draft';
  }
  if (status.mergeQueue === 'queued') {
    return 'Queued to merge';
  }

  if (!status.approvalStatus.passed) {
    if (status.approvalStatus.hasBlockingChangeRequests) {
      return 'Changes requested';
    }
    if (status.approvalStatus.requiredApprovalCount === 1) {
      return 'Approval required';
    }
    return 'Approvals required';
  }
  // Ahead of the `mergeable` fallback: GitHub only reports BLOCKED once it has ruled out
  // conflicts, so a mirror that has not recorded `mergeable` yet should not read as conflicting.
  if (isBlockedByBranchRules(status)) {
    return 'Blocked by branch rules';
  }
  if (!status.mergeConflicts.passed || !status.state.mergeable) {
    return 'Merge conflicts';
  }
  if (!status.checksStatus.passed) {
    if (inProgressRequiredChecks.length > 0) {
      return status.viewer.canBypassProtections
        ? 'Required CI in progress (admin bypass available)'
        : 'Required CI in progress';
    }

    return status.viewer.canBypassProtections
      ? 'Required CI failed (admin bypass available)'
      : 'Required CI failed';
  }
  if (status.state.prState !== 'open') {
    return status.state.prState.charAt(0).toUpperCase() + status.state.prState.slice(1);
  }
  return 'Ready to merge';
};

export type MergeQueueState = 'none' | 'required' | 'queued';

export type MergeabilityStatus = {
  pullRequestId: string;
  canMerge: boolean;
  viewer: {
    permission: RepositoryPermission | null;
    isAdmin: boolean;
    canBypassProtections: boolean; // admin and enforce_admins disabled
    canEnableAutoMerge: boolean;
  };
  autoMerge: {
    canEnable: boolean;
    alreadyEnabled: boolean;
    enabledMethod: ('MERGE' | 'SQUASH' | 'REBASE') | null;
    repositoryAllowsAutoMerge: boolean;
    allowedMethods: Array<'MERGE' | 'SQUASH' | 'REBASE'>;
    reasons?: string[];
  };
  canAdminsBypassRules: boolean; // repo-level capability
  checksStatus: {
    passed: boolean;
    requiredChecks: string[];
    nonPassedRequiredChecks?: string[];
  };
  approvalStatus: {
    passed: boolean;
    requiredApprovalCount: number;
    currentApprovalCount: number;
    hasBlockingChangeRequests: boolean;
  };
  mergeConflicts: {
    passed: boolean;
    hasConflicts: boolean;
  };
  state: {
    merged: boolean;
    mergeable: boolean | null;
    prState: string;
  };
  /**
   * GitHub's overall merge verdict. BLOCKED also covers rules the fields above don't model,
   * such as required code owner review, conversation resolution, or deployments.
   */
  mergeStateStatus: MergeStateStatus;
  /**
   * Whether the base branch merges through a merge queue, and whether this pull request is in it.
   * A ready pull request on a queue branch reports CLEAN, so the verdict above can't tell.
   */
  mergeQueue: MergeQueueState;
  /**
   * Position in a GitHub stack, 1 at the bottom, or null outside a stack. Merging a stacked pull
   * request also merges every open pull request below it.
   */
  stackPosition: number | null;
  requiresLinearHistory: boolean;
  baseBranch: string | null;
  draft: boolean;
  humanReadableMergeStatus: string;
};

/** Everything in a status except the two fields `withMergeReadiness` derives. */
export type MergeabilityFacts = Omit<MergeabilityStatus, 'canMerge' | 'humanReadableMergeStatus'>;

/**
 * True when GitHub blocks the merge although every requirement modeled here passes, so the
 * block comes from a rule only GitHub evaluates.
 */
export function isBlockedByBranchRules(
  status: Pick<
    MergeabilityFacts,
    'mergeStateStatus' | 'approvalStatus' | 'mergeConflicts' | 'checksStatus'
  >,
): boolean {
  return (
    status.mergeStateStatus === MergeStateStatus.Blocked &&
    status.approvalStatus.passed &&
    status.mergeConflicts.passed &&
    status.checksStatus.passed
  );
}

/**
 * Derives `canMerge` and `humanReadableMergeStatus` from the rest of the status.
 * The server mirror and the client's optimistic cache updates both go through here, so they agree.
 * `inProgressRequiredChecks` names the required checks whose newest run hasn't finished.
 */
export function withMergeReadiness(
  facts: MergeabilityFacts,
  inProgressRequiredChecks: readonly string[],
): MergeabilityStatus {
  return {
    ...facts,
    canMerge:
      !facts.state.merged &&
      facts.mergeConflicts.passed &&
      facts.approvalStatus.passed &&
      facts.checksStatus.passed &&
      !facts.draft &&
      facts.state.mergeable === true &&
      facts.state.prState === 'open' &&
      facts.mergeStateStatus !== MergeStateStatus.Blocked &&
      facts.mergeQueue !== 'queued',
    humanReadableMergeStatus: getHumanReadableMergeStatus(facts, inProgressRequiredChecks),
  };
}
